/* SAP Conversion — theme.js
   Vanilla JS, no dependencies. Everything is progressive enhancement:
   forms post normally when this file has not loaded. */
(() => {
  'use strict';

  const theme = window.theme || {};
  const routes = theme.routes || {};
  const strings = theme.strings || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const root = (path) => (routes.root || '/').replace(/\/$/, '') + path;

  /* ---------------------------------------------------------------------
     Money
     --------------------------------------------------------------------- */
  function formatMoney(cents) {
    const format = theme.moneyFormat || '₹{{amount}}';
    const value = (Number(cents) || 0) / 100;
    const group = (n, sep, dec) => {
      const [int, frac] = n.toFixed(2).split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
      return frac === '00' ? grouped : grouped + dec + frac;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(Math.round(value), ',', '.');
        case 'amount_with_comma_separator': return group(value, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(Math.round(value), '.', ',');
        case 'amount_with_apostrophe_separator': return group(value, "'", '.');
        case 'amount_with_space_separator': return group(value, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return group(Math.round(value), ' ', ',');
        default: return group(value, ',', '.');
      }
    }).replace(/<[^>]*>/g, '');
  }

  /* ---------------------------------------------------------------------
     Small helpers
     --------------------------------------------------------------------- */
  const debounce = (fn, wait) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  function replay(el, cls) {
    if (!el || reduceMotion) return;
    el.classList.remove(cls);
    void el.offsetWidth; // restart the animation
    el.classList.add(cls);
    el.addEventListener('animationend', () => el.classList.remove(cls), { once: true });
  }

  let toastTimer;
  function toast(message) {
    const el = $('#Toast');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    replay(el, 'is-shown');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.hidden = true; }, 3200);
  }

  const lockers = new Set();
  function lockScroll(key, on) {
    on ? lockers.add(key) : lockers.delete(key);
    document.documentElement.classList.toggle('is-locked', lockers.size > 0);
  }

  function trapFocus(container, event) {
    const focusable = $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', container)
      .filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { last.focus(); event.preventDefault(); }
    else if (!event.shiftKey && document.activeElement === last) { first.focus(); event.preventDefault(); }
  }

  /* ---------------------------------------------------------------------
     Cart state + rendering
     --------------------------------------------------------------------- */
  const Cart = {
    count: 0,
    total: 0,
    sectionId: 'cart-drawer',

    get drawer() { return $('#CartDrawer'); },

    readSummary(scope = document) {
      const node = $('[data-cart-summary]', scope);
      if (!node) return null;
      try { return JSON.parse(node.textContent); } catch (e) { return null; }
    },

    init() {
      const summary = this.readSummary();
      if (summary) { this.count = summary.count; this.total = summary.total; this.paintCards(summary); }
      this.bindDrawer();
    },

    async request(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) {
        const err = new Error(data.description || data.message || strings.error);
        err.data = data;
        throw err;
      }
      return data;
    },

    async add(formData) {
      formData.append('sections', this.sectionId);
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(routes.cartAdd + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || strings.error);
      }
      this.render(data.sections, { added: data.key });
      return data;
    },

    async change(line, quantity) {
      const drawer = this.drawer;
      drawer && drawer.classList.add('is-loading');
      try {
        const data = await this.request(routes.cartChange + '.js', {
          line, quantity, sections: [this.sectionId], sections_url: window.location.pathname
        });
        this.render(data.sections);
      } catch (e) {
        toast(e.message);
        this.refresh();
      } finally {
        drawer && drawer.classList.remove('is-loading');
      }
    },

    async refresh() {
      const res = await fetch(`${root('/')}?section_id=${this.sectionId}`);
      if (res.ok) this.render({ [this.sectionId]: await res.text() });
    },

    render(sections, opts = {}) {
      const html = sections && sections[this.sectionId];
      const drawer = this.drawer;
      if (!html || !drawer) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $('#CartDrawer', doc);
      if (!fresh) return;

      const panel = $('.drawer__panel', drawer);
      const prevKeys = new Set($$('[data-key]', drawer).map((n) => n.dataset.key));
      const wasLocked = $('.ship', drawer) && !$('.ship', drawer).classList.contains('is-unlocked');
      const scrollTop = $('.drawer__body', drawer)?.scrollTop || 0;

      // Keep the old progress width so the bar animates from where it was.
      const oldFill = $('[data-ship-fill]', drawer);
      const oldPct = oldFill ? oldFill.style.getPropertyValue('--pct') : null;

      panel.innerHTML = $('.drawer__panel', fresh).innerHTML;
      drawer.toggleAttribute('data-empty', fresh.hasAttribute('data-empty'));

      const body = $('.drawer__body', drawer);
      if (body) body.scrollTop = scrollTop;

      const newFill = $('[data-ship-fill]', drawer);
      if (newFill && oldPct) {
        const target = newFill.style.getPropertyValue('--pct');
        newFill.style.setProperty('--pct', oldPct);
        requestAnimationFrame(() => requestAnimationFrame(() => newFill.style.setProperty('--pct', target)));
      }

      // Highlight a newly added / changed line
      $$('[data-key]', drawer).forEach((li) => {
        if (!prevKeys.has(li.dataset.key) || li.dataset.key === opts.added) li.classList.add('is-new');
      });
      if (opts.added) {
        const line = $(`[data-key="${CSS.escape(opts.added)}"]`, drawer);
        line && line.scrollIntoView({ block: 'nearest' });
      }

      const ship = $('.ship', drawer);
      if (wasLocked && ship && ship.classList.contains('is-unlocked')) replay(ship, 'just-unlocked');

      const summary = this.readSummary(drawer);
      if (summary) this.update(summary);
    },

    update(summary) {
      const grew = summary.count > this.count;
      this.count = summary.count;
      this.total = summary.total;

      $$('[data-cart-count]').forEach((el) => {
        el.textContent = summary.count;
        el.toggleAttribute('data-empty', summary.count === 0);
        if (grew) replay(el, 'is-bumping');
      });
      if (grew) replay($('.cart-icon'), 'is-bumping');
      StickyBar.update(summary);
      this.paintCards(summary);
    },

    paintCards(summary) {
      const qty = {};
      Object.values(summary.items || {}).forEach(([id, q]) => { qty[id] = (qty[id] || 0) + q; });
      $$('[data-product-card]').forEach((card) => {
        const label = $('[data-in-cart]', card);
        if (!label) return;
        const n = qty[card.dataset.productId] || 0;
        label.hidden = n === 0;
        if (n) label.textContent = `✓ ${n} in cart`;
      });
    },

    /* Drawer UI */
    open() {
      const drawer = this.drawer;
      if (!drawer) { window.location.href = routes.cart; return; }
      if (drawer.classList.contains('is-open')) return;
      this.lastFocus = document.activeElement;
      closeQuickAdds();
      drawer.classList.add('is-open');
      lockScroll('drawer', true);
      const panel = $('.drawer__panel', drawer);
      setTimeout(() => panel && panel.focus({ preventScroll: true }), 50);
    },

    close() {
      const drawer = this.drawer;
      if (!drawer || !drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      lockScroll('drawer', false);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
    },

    bindDrawer() {
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-cart-open]');
        if (opener && theme.cartAction !== 'page') { e.preventDefault(); this.open(); return; }
        if (e.target.closest('[data-cart-close]')) { e.preventDefault(); this.close(); return; }
        if (e.target.closest('[data-cart-continue]') && this.drawer?.classList.contains('is-open')) {
          // Stay on the page when it already lists products.
          if ($('[data-grid], [data-collection-results]')) { e.preventDefault(); this.close(); }
          return;
        }
        const changer = e.target.closest('[data-line-change]');
        if (changer) {
          e.preventDefault();
          const li = changer.closest('[data-line]');
          const qty = Number(changer.dataset.lineChange);
          if (qty === 0 && li && !reduceMotion) li.classList.add('is-removing');
          const value = li && $('.qty__value', li);
          if (value && qty > 0) { value.textContent = qty; replay(value, 'is-ticking'); }
          this.change(Number(li.dataset.line), qty);
        }
      });

      document.addEventListener('keydown', (e) => {
        const drawer = this.drawer;
        if (!drawer || !drawer.classList.contains('is-open')) return;
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') trapFocus($('.drawer__panel', drawer), e);
      });

      // Swipe the bottom sheet down to close (mobile)
      let startY = null, dy = 0;
      document.addEventListener('touchstart', (e) => {
        const drawer = this.drawer;
        if (!drawer || !drawer.classList.contains('is-open')) return;
        const head = e.target.closest('.drawer__handle, .drawer__head');
        if (!head || window.innerWidth >= 750) return;
        startY = e.touches[0].clientY; dy = 0;
        drawer.classList.add('is-dragging');
      }, { passive: true });
      document.addEventListener('touchmove', (e) => {
        if (startY === null) return;
        dy = Math.max(0, e.touches[0].clientY - startY);
        $('.drawer__panel', this.drawer).style.transform = `translateY(${dy}px)`;
      }, { passive: true });
      document.addEventListener('touchend', () => {
        if (startY === null) return;
        const drawer = this.drawer;
        drawer.classList.remove('is-dragging');
        $('.drawer__panel', drawer).style.transform = '';
        if (dy > 90) this.close();
        startY = null;
      });
    }
  };

  /* ---------------------------------------------------------------------
     Add to cart (cards, product form, upsell)
     --------------------------------------------------------------------- */
  function setButtonState(btn, state) {
    if (!btn) return;
    const label = $('.btn__label', btn);
    if (state === 'loading') {
      btn.dataset.html = btn.dataset.html || (label ? label.innerHTML : btn.innerHTML);
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      return;
    }
    btn.classList.remove('is-loading');
    btn.removeAttribute('aria-busy');
    if (state === 'added' && label) {
      label.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>' + (strings.added || 'Added');
      btn.classList.add('is-added');
      setTimeout(() => {
        btn.classList.remove('is-added');
        if (btn.dataset.html) label.innerHTML = btn.dataset.html;
      }, 1600);
    } else if (state === 'error') {
      replay(btn, 'is-error');
    }
  }

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form || theme.cartAction === 'page') return;
    e.preventDefault();

    const submitter = e.submitter || $('[type="submit"]', form);
    if (submitter && (submitter.disabled || submitter.classList.contains('is-loading'))) return;

    const fd = new FormData(form);
    if (submitter && submitter.name === 'id') fd.set('id', submitter.value);
    if (!fd.get('id')) {
      // e.g. upsell without a chosen size: point at the picker
      const select = $('select[name="id"]', form);
      if (select) { select.focus(); replay(select, 'is-error'); }
      return;
    }

    // Size buttons spin themselves; otherwise spin the main button.
    const isSize = submitter && submitter.classList.contains('size-btn');
    const mainBtn = isSize ? submitter : (submitter || $('[data-add-button]', form));
    if (isSize) submitter.classList.add('is-loading');
    else setButtonState(mainBtn, 'loading');

    const stickyBtn = form.matches('[data-main-form]') ? $('[data-cbar-atc]') : null;
    if (stickyBtn) setButtonState(stickyBtn, 'loading');

    try {
      await Cart.add(fd);
      if (isSize) {
        submitter.classList.remove('is-loading');
        const details = form.closest('[data-quick-add]');
        if (details) {
          details.open = false;
          setButtonState($('summary', details), 'added');
        }
      } else {
        setButtonState(mainBtn, 'added');
      }
      if (stickyBtn) setButtonState(stickyBtn, 'added');
      replay($('[data-cbar-checkout]'), 'is-shining');

      if (theme.cartAction === 'checkout') { window.location.href = root('/checkout'); return; }
      Cart.open();
    } catch (err) {
      if (isSize) submitter.classList.remove('is-loading');
      setButtonState(mainBtn, 'error');
      if (stickyBtn) setButtonState(stickyBtn, 'error');
      toast(err.message || strings.error);
    }
  });

  /* ---------------------------------------------------------------------
     Quick add size picker on cards
     --------------------------------------------------------------------- */
  function closeQuickAdds(except) {
    $$('[data-quick-add][open]').forEach((d) => { if (d !== except) d.open = false; });
  }
  document.addEventListener('toggle', (e) => {
    const d = e.target;
    if (!(d instanceof HTMLDetailsElement)) return;
    if (d.matches('[data-quick-add]') && d.open) {
      closeQuickAdds(d);
      const firstSize = $('.size-btn:not(:disabled)', d);
      firstSize && firstSize.focus({ preventScroll: true });
    }
  }, true);
  document.addEventListener('click', (e) => {
    const close = e.target.closest('[data-quick-close]');
    if (close) {
      const d = close.closest('[data-quick-add]');
      d.open = false;
      $('summary', d).focus();
      return;
    }
    if (!e.target.closest('[data-quick-add]')) closeQuickAdds();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = $('[data-quick-add][open]');
    if (open) { open.open = false; $('summary', open).focus(); }
  });

  /* ---------------------------------------------------------------------
     Sticky checkout bar
     --------------------------------------------------------------------- */
  const StickyBar = {
    el: null,
    init() {
      this.el = $('[data-cbar]');
      if (!this.el) return;
      const setHeight = () => document.body.style.setProperty('--cbar-h', this.el.offsetHeight + 'px');
      setHeight();
      if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(this.el);
      requestAnimationFrame(() => setTimeout(() => this.el.classList.add('is-ready'), 250));

      // Product page: only offer the bar's Add to cart once the main one is off screen.
      const mainAtc = $('[data-main-atc]');
      if (mainAtc && 'IntersectionObserver' in window && this.el.classList.contains('cbar--product')) {
        new IntersectionObserver(([en]) => {
          this.el.classList.toggle('is-main-visible', en.isIntersecting);
        }).observe(mainAtc);
      }

      this.el.addEventListener('click', (e) => {
        const checkout = e.target.closest('[data-cbar-checkout]');
        if (checkout) {
          e.preventDefault();
          if (Cart.count > 0) {
            checkout.classList.add('is-loading');
            window.location.href = root('/checkout');
          } else if (!this.el.classList.contains('cbar--product')) {
            // Empty cart: take the shopper to products instead of an empty drawer.
            const grid = $('[data-grid-section]');
            if (grid) grid.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
            else window.location.href = root('/collections/all');
          } else if (theme.cartAction !== 'page') {
            const mainForm = $('[data-main-form]');
            // On a product page an empty-cart checkout means "buy this now".
            if (mainForm && !$('[data-main-atc]', mainForm)?.disabled) {
              this.buyNow(mainForm, checkout);
            } else {
              Cart.open();
            }
          } else {
            window.location.href = routes.cart;
          }
          return;
        }
        const atc = e.target.closest('[data-cbar-atc]');
        if (atc) {
          const mainForm = $('[data-main-form]');
          const mainBtn = mainForm && $('[data-main-atc]', mainForm);
          if (mainForm && mainBtn && !mainBtn.disabled) mainForm.requestSubmit(mainBtn);
        }
      });
    },

    async buyNow(form, btn) {
      btn.classList.add('is-loading');
      try {
        const fd = new FormData(form);
        await Cart.add(fd);
        window.location.href = root('/checkout');
      } catch (err) {
        btn.classList.remove('is-loading');
        toast(err.message);
      }
    },

    update(summary) {
      if (!this.el) return;
      const threshold = Number(theme.freeShippingThreshold) || 0;
      const left = Math.max(0, threshold - summary.total);
      const pct = threshold > 0 ? Math.min(100, Math.round((summary.total / threshold) * 100)) : 100;
      const tpl = this.el.dataset;
      this.el.toggleAttribute('data-empty', summary.count === 0);

      const title = $('[data-bar-title]', this.el);
      if (title) {
        title.textContent = summary.count === 0
          ? (tpl.emptyText || title.textContent)
          : `${summary.count} ${summary.count === 1 ? (tpl.itemOne || 'item') : (tpl.itemOther || 'items')} · ${formatMoney(summary.total)}`;
      }
      const total = $('[data-bar-total]', this.el);
      if (total) total.textContent = formatMoney(summary.total);

      const ship = $('[data-bar-ship]', this.el);
      if (ship && threshold > 0) {
        if (summary.count === 0) ship.textContent = (tpl.shipHint || '').replace('[amount]', formatMoney(threshold));
        else if (left > 0) ship.innerHTML = (tpl.shipLeft || '').replace('[amount]', `<strong>${formatMoney(left)}</strong>`);
        else ship.textContent = tpl.shipDone || '';
      }
      const track = $('[data-bar-track]', this.el);
      if (track) {
        track.classList.toggle('is-unlocked', left <= 0);
        const fill = $('[data-ship-fill]', track);
        fill && fill.style.setProperty('--pct', pct + '%');
      }
    }
  };

  /* ---------------------------------------------------------------------
     Product page: variants, quantity, gallery
     --------------------------------------------------------------------- */
  function initProduct(section) {
    if (!section || section.dataset.ready) return;
    section.dataset.ready = '1';
    const jsonNode = $('[data-product-json]', section);
    const form = $('[data-main-form]', section);
    if (!jsonNode || !form) return;
    const product = JSON.parse(jsonNode.textContent);
    const idInput = $('[data-variant-id]', form);
    const atc = $('[data-main-atc]', form);
    const priceWrap = $('[data-product-price]', section);
    const qtyInput = $('[data-qty-input]', section);

    const selected = () => $$('[data-option-index]', section).map((fs) => {
      const checked = $('input:checked', fs);
      return checked ? checked.value : null;
    });

    function updateAvailability(current) {
      // Mark values that cannot be bought given the other selected options.
      $$('[data-option-index]', section).forEach((fs) => {
        const idx = Number(fs.dataset.optionIndex);
        $$('[data-option-input]', fs).forEach((input) => {
          const combo = current.slice();
          combo[idx] = input.value;
          const ok = product.variants.some((v) => v.available && v.options.every((o, i) => o === combo[i]));
          input.closest('.swatch').classList.toggle('is-unavailable', !ok);
        });
      });
    }

    function onChange() {
      const opts = selected();
      const variant = product.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      $$('[data-option-index]', section).forEach((fs, i) => {
        const v = $('[data-option-value]', fs);
        if (v) v.textContent = opts[i];
      });
      updateAvailability(opts);

      const label = $('[data-atc-label]', atc);
      const cbarAtc = $('[data-cbar-atc]');
      const cbarLabel = cbarAtc && $('.btn__label', cbarAtc);

      if (!variant) {
        atc.disabled = true;
        label.textContent = strings.unavailable;
        if (cbarAtc) { cbarAtc.disabled = true; cbarLabel.textContent = strings.unavailable; }
        return;
      }

      idInput.value = variant.id;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      atc.disabled = !variant.available;
      delete atc.dataset.html;
      label.innerHTML = variant.available
        ? `${strings.addToCart} · <span data-atc-price>${formatMoney(variant.price)}</span>`
        : strings.soldOut;
      if (cbarAtc) {
        cbarAtc.disabled = !variant.available;
        delete cbarAtc.dataset.html;
        cbarLabel.textContent = variant.available ? strings.addToCart : strings.soldOut;
      }

      // Price block
      const price = $('.price', priceWrap);
      if (price) {
        const sale = variant.compare_at_price > variant.price;
        price.classList.toggle('price--sale', sale);
        $('[data-price]', price).textContent = formatMoney(variant.price);
        const cmp = $('[data-compare-price]', price);
        const save = $('[data-save]', price);
        if (cmp) { cmp.hidden = !sale; cmp.lastChild.textContent = formatMoney(variant.compare_at_price); }
        if (save) {
          save.hidden = !sale;
          save.textContent = save.textContent.replace(/[^\s]*\d[\d,.]*/, formatMoney(variant.compare_at_price - variant.price));
        }
        replay(price, 'is-updating');
      }

      // Quantity cap + accelerated checkout availability
      if (qtyInput) {
        if (variant.inventory !== null && variant.inventory > 0) qtyInput.max = variant.inventory; else qtyInput.removeAttribute('max');
        if (qtyInput.max && Number(qtyInput.value) > Number(qtyInput.max)) qtyInput.value = qtyInput.max;
      }
      const buyNow = $('[data-buy-now]', section);
      if (buyNow) buyNow.hidden = !variant.available;

      // Stock
      const stock = $('[data-stock]', section);
      const threshold = Number(section.dataset.lowStock || 5);
      if (stock) {
        const n = variant.inventory;
        const low = variant.available && n !== null && n > 0 && n <= threshold;
        stock.hidden = !low;
        if (low) $('[data-stock-text]', stock).textContent = $('[data-stock-text]', stock).textContent.replace(/\d+/, n);
      }

      // Sticky bar
      const cv = $('[data-cbar-variant]');
      const cp = $('[data-cbar-price]');
      if (cv) cv.textContent = variant.title;
      if (cp) cp.textContent = formatMoney(variant.price);

      // Jump the gallery to the variant image
      if (variant.media_id) {
        const media = $(`[data-media-id="${variant.media_id}"]`, section);
        const track = $('[data-gallery-track]', section);
        if (media && track) {
          if (window.innerWidth < 750) track.scrollTo({ left: media.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
          else if (media.getBoundingClientRect().top < 0 || media.getBoundingClientRect().top > window.innerHeight) media.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        }
      }
    }

    section.addEventListener('change', (e) => { if (e.target.matches('[data-option-input]')) onChange(); });
    updateAvailability(selected());

    // Quantity stepper
    section.addEventListener('click', (e) => {
      const step = e.target.closest('[data-qty-step]');
      if (!step || !qtyInput) return;
      const max = Number(qtyInput.max) || Infinity;
      const next = (parseInt(qtyInput.value, 10) || 1) + Number(step.dataset.qtyStep);
      if (next > max) { toast(($('[data-stock-text]', section)?.textContent || '').trim() || strings.error); replay(qtyInput.closest('.qty'), 'is-error'); }
      qtyInput.value = Math.min(max, Math.max(1, next));
      replay(qtyInput, 'is-ticking');
    });

    initGallery(section);
  }

  function initGallery(scope) {
    const track = $('[data-gallery-track]', scope);
    if (!track) return;
    const dots = $$('.gallery__dot', scope);
    if (!dots.length) return;
    const onScroll = () => {
      const i = Math.round(track.scrollLeft / track.clientWidth);
      dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
    };
    track.addEventListener('scroll', debounce(onScroll, 40), { passive: true });
  }

  /* ---------------------------------------------------------------------
     Home grid: filter + sort with a FLIP animation
     --------------------------------------------------------------------- */
  function initGrid(section) {
    const grid = $('[data-grid]', section);
    if (!grid || grid.dataset.ready) return;
    grid.dataset.ready = '1';
    const cards = $$('[data-product-card]', grid);
    const original = cards.slice();
    const empty = $('[data-grid-empty]', section);
    const count = $('[data-grid-count]', section);
    let filter = null;
    let sort = 'featured';

    const test = (card) => {
      if (!filter || filter.dataset.filter === 'all') return true;
      const price = Number(card.dataset.price);
      switch (filter.dataset.filter) {
        case 'price': {
          const min = Number(filter.dataset.min) || 0;
          const max = Number(filter.dataset.max) || Infinity;
          return price >= min && price <= (max || Infinity) + 99;
        }
        case 'sale': return Number(card.dataset.compare) > price;
        default: {
          const v = (filter.dataset.value || '').trim();
          return !v || card.dataset.tags.split('|').some((t) => t === v || t.includes(v)) || card.dataset.title.includes(v);
        }
      }
    };

    const sorters = {
      featured: (a, b) => original.indexOf(a) - original.indexOf(b),
      'price-asc': (a, b) => a.dataset.price - b.dataset.price,
      savings: (a, b) => (b.dataset.compare - b.dataset.price) / b.dataset.compare - (a.dataset.compare - a.dataset.price) / a.dataset.compare
    };

    function apply() {
      const first = new Map(cards.map((c) => [c, c.getBoundingClientRect()]));
      const sorted = cards.slice().sort(sorters[sort]);
      let visible = 0;
      sorted.forEach((c) => {
        const show = test(c);
        c.classList.toggle('is-hiding', !show);
        if (show) visible += 1;
        grid.appendChild(c);
      });
      if (empty) empty.hidden = visible > 0;
      if (count) count.textContent = visible;

      if (reduceMotion) return;
      sorted.forEach((c) => {
        if (c.classList.contains('is-hiding')) return;
        c.classList.add('is-in');
        const a = first.get(c);
        const b = c.getBoundingClientRect();
        if (!a || a.width === 0) {
          c.animate([{ opacity: 0, transform: 'scale(.96)' }, { opacity: 1, transform: 'none' }], { duration: 320, easing: 'cubic-bezier(.2,.8,.2,1)' });
          return;
        }
        const dx = a.left - b.left;
        const dy = a.top - b.top;
        if (dx || dy) c.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], { duration: 420, easing: 'cubic-bezier(.2,.8,.2,1)' });
      });
    }

    const pick = (group, btn) => {
      $$(group, section).forEach((b) => { const on = b === btn; b.classList.toggle('is-active', on); b.setAttribute('aria-pressed', on); });
    };

    section.addEventListener('click', (e) => {
      const f = e.target.closest('[data-filter]');
      if (f) { filter = f; pick('[data-filter]', f); apply(); return; }
      const s = e.target.closest('[data-sort]');
      if (s) { sort = s.dataset.sort; pick('[data-sort]', s); apply(); return; }
      if (e.target.closest('[data-filter-reset]')) {
        const all = $('[data-filter="all"]', section);
        filter = all; pick('[data-filter]', all); apply();
      }
    });

    section.applyFilterById = (id) => {
      const btn = $(`[data-filter-id="${CSS.escape(id)}"]`, section);
      if (btn) btn.click();
    };
  }

  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-apply-filter]');
    if (!link) return;
    const hash = (link.getAttribute('href') || '').split('#')[1];
    const target = hash ? document.getElementById(hash) : $('[data-grid-section]');
    const section = target && (target.matches('[data-grid-section]') ? target : $('[data-grid-section]', target));
    if (!section || !section.applyFilterById) return;
    e.preventDefault();
    section.applyFilterById(link.dataset.applyFilter);
    section.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  });

  /* ---------------------------------------------------------------------
     Collection page: sort, facets and "load more" without reloads
     --------------------------------------------------------------------- */
  function initCollection(section) {
    if (!section || section.dataset.ready) return;
    section.dataset.ready = '1';
    const id = section.dataset.sectionId;

    async function load(url, { append = false, push = true } = {}) {
      const results = $('[data-collection-results]', section);
      results.classList.add('is-loading');
      try {
        const u = new URL(url, window.location.origin);
        u.searchParams.set('section_id', id);
        const res = await fetch(u.toString());
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = $('[data-collection-results]', doc);
        if (append) {
          const grid = $('.grid', results);
          $$('.grid > *', fresh).forEach((card, i) => { card.style.setProperty('--i', i % 8); grid.appendChild(card); });
          const nav = $('.pagination', results);
          const freshNav = $('.pagination', fresh);
          nav && (freshNav ? nav.replaceWith(freshNav) : nav.remove());
        } else {
          results.innerHTML = fresh.innerHTML;
          const facets = $('[data-facets]', section);
          const freshFacets = $('[data-facets]', doc);
          if (facets && freshFacets) facets.innerHTML = freshFacets.innerHTML;
        }
        observeReveals(section);
        Cart.paintCards({ items: (Cart.readSummary() || {}).items });
        if (push) {
          u.searchParams.delete('section_id');
          if (append) u.searchParams.delete('page');
          window.history.pushState({}, '', u.toString());
        }
      } catch (e) {
        window.location.href = url;
      } finally {
        results.classList.remove('is-loading');
      }
    }

    section.addEventListener('click', (e) => {
      const facet = e.target.closest('[data-facet-link]');
      if (facet) { e.preventDefault(); load(facet.href); return; }
      const more = e.target.closest('[data-load-more]');
      if (more) { e.preventDefault(); more.classList.add('is-loading'); load(more.href, { append: true, push: false }); }
    });
    const select = $('[data-sort-select]', section);
    if (select) select.addEventListener('change', () => {
      const u = new URL(window.location.href);
      u.searchParams.set('sort_by', select.value);
      u.searchParams.delete('page');
      load(u.toString());
    });
    window.addEventListener('popstate', () => load(window.location.href, { push: false }));
  }

  /* ---------------------------------------------------------------------
     Related products (lazy, after first paint)
     --------------------------------------------------------------------- */
  function initRecommendations(section) {
    if (!section || section.dataset.ready || !section.dataset.url) return;
    section.dataset.ready = '1';
    const load = async () => {
      try {
        const res = await fetch(section.dataset.url);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = $('[data-recommendations]', doc);
        if (fresh && fresh.innerHTML.trim()) {
          section.innerHTML = fresh.innerHTML;
          observeReveals(section);
          Cart.paintCards(Cart.readSummary() || {});
        } else section.remove();
      } catch (e) { section.remove(); }
    };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        if (entries.some((en) => en.isIntersecting)) { io.disconnect(); load(); }
      }, { rootMargin: '400px' });
      io.observe(section);
    } else load();
  }

  /* ---------------------------------------------------------------------
     Header: shadow on scroll, mobile menu, search + predictive results
     --------------------------------------------------------------------- */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    const setVars = () => {
      const r = header.getBoundingClientRect();
      document.documentElement.style.setProperty('--header-bottom', Math.max(0, r.bottom) + 'px');
    };
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { header.classList.toggle('is-scrolled', window.scrollY > 8); ticking = false; });
    }, { passive: true });
    setVars();
    window.addEventListener('resize', debounce(setVars, 100));

    const menu = $('[data-menu-drawer]', header);
    if (menu) {
      menu.addEventListener('toggle', () => {
        setVars();
        lockScroll('menu', menu.open);
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && menu.open) { menu.open = false; $('summary', menu).focus(); } });
    }

    const search = $('[data-search]', header);
    if (!search) return;
    const input = $('[data-search-input]', search);
    const results = $('[data-predictive]', search);
    let controller;

    $('[data-search-open]', header)?.addEventListener('click', () => {
      search.classList.add('is-open');
      setTimeout(() => input.focus(), 60);
    });
    $('[data-search-close]', search)?.addEventListener('click', () => {
      search.classList.remove('is-open');
      results.hidden = true;
    });

    const fetchResults = debounce(async (q) => {
      if (controller) controller.abort();
      if (q.length < 2) { results.hidden = true; input.setAttribute('aria-expanded', 'false'); return; }
      controller = new AbortController();
      try {
        const url = `${routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
        const res = await fetch(url, { signal: controller.signal });
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const inner = $('[data-predictive-results]', doc);
        results.innerHTML = inner ? inner.outerHTML : '';
        results.hidden = !inner;
        input.setAttribute('aria-expanded', String(!!inner));
      } catch (e) { /* aborted */ }
    }, 220);

    input.addEventListener('input', () => fetchResults(input.value.trim()));
    input.addEventListener('focus', () => { if (results.innerHTML.trim() && input.value.trim().length > 1) results.hidden = false; });
    input.addEventListener('keydown', (e) => {
      const items = $$('.predictive__item, .predictive__all', results);
      if (!items.length || results.hidden) return;
      const i = items.findIndex((n) => n.classList.contains('is-focused'));
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const next = e.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
        items.forEach((n, k) => n.classList.toggle('is-focused', k === next));
        items[next].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter' && i > -1) {
        e.preventDefault();
        window.location.href = items[i].href;
      } else if (e.key === 'Escape') {
        results.hidden = true;
      }
    });
    document.addEventListener('click', (e) => { if (!search.contains(e.target) && !e.target.closest('[data-search-open]')) results.hidden = true; });
  }

  /* ---------------------------------------------------------------------
     Smooth <details> accordions
     --------------------------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const summary = e.target.closest('[data-accordion] > summary');
    if (!summary || reduceMotion) return;
    const details = summary.parentElement;
    const content = summary.nextElementSibling;
    if (!content || details.classList.contains('is-animating')) { e.preventDefault(); return; }
    e.preventDefault();
    details.classList.add('is-animating');
    const start = details.offsetHeight;
    const opening = !details.open;
    if (opening) details.open = true;
    const end = opening ? summary.offsetHeight + content.offsetHeight : summary.offsetHeight;
    const anim = details.animate({ height: [start + 'px', end + 'px'] }, { duration: 300, easing: 'cubic-bezier(.2,.8,.2,1)' });
    if (opening) content.animate([{ opacity: 0, transform: 'translateY(-4px)' }, { opacity: 1, transform: 'none' }], { duration: 300, easing: 'ease' });
    anim.onfinish = () => {
      if (!opening) details.open = false;
      details.classList.remove('is-animating');
    };
  });

  /* ---------------------------------------------------------------------
     Size guide modal
     --------------------------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-modal-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.modalOpen);
      if (dialog && dialog.showModal) { dialog.showModal(); lockScroll('modal', true); }
      return;
    }
    const closer = e.target.closest('[data-modal-close]');
    if (closer) { closer.closest('dialog').close(); return; }
    if (e.target instanceof HTMLDialogElement && e.target.open) e.target.close(); // backdrop click
  });
  document.addEventListener('close', (e) => { if (e.target instanceof HTMLDialogElement) lockScroll('modal', false); }, true);

  /* ---------------------------------------------------------------------
     Cart page quantity inputs: update on change
     --------------------------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-cart-page-stepper] [data-step]');
    if (!step) return;
    const input = $('[data-cart-page-qty]', step.parentElement);
    input.value = Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.dataset.step));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-cart-page-qty]');
    if (!input) return;
    const form = input.form;
    form.classList.add('is-loading');
    form.requestSubmit ? form.requestSubmit() : form.submit();
  });

  /* ---------------------------------------------------------------------
     Reveal on scroll
     --------------------------------------------------------------------- */
  let revealObserver;
  function observeReveals(scope = document) {
    const items = $$('.reveal:not(.is-in)', scope);
    if (!('IntersectionObserver' in window) || reduceMotion) { items.forEach((n) => n.classList.add('is-in')); return; }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) { en.target.classList.add('is-in'); revealObserver.unobserve(en.target); }
        });
      }, { rootMargin: '0px 0px -40px 0px', threshold: 0.05 });
    }
    items.forEach((n) => revealObserver.observe(n));
  }

  /* ---------------------------------------------------------------------
     Announcement rotation
     --------------------------------------------------------------------- */
  function initAnnouncement() {
    const track = $('[data-announcement]');
    if (!track || !track.classList.contains('announcement__track--rotate')) return;
    const items = $$('.announcement__item', track);
    let i = 0;
    setInterval(() => {
      if (document.hidden) return;
      const cur = items[i];
      i = (i + 1) % items.length;
      cur.classList.add('is-leaving');
      cur.classList.remove('is-active');
      items[i].classList.remove('is-leaving');
      items[i].classList.add('is-active');
      setTimeout(() => cur.classList.remove('is-leaving'), 600);
    }, 4500);
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  function initSections(scope = document) {
    $$('[data-product-section]', scope).forEach(initProduct);
    $$('[data-grid-section]', scope).forEach(initGrid);
    $$('[data-collection-section]', scope).forEach(initCollection);
    $$('[data-recommendations]', scope).forEach(initRecommendations);
    observeReveals(scope);
  }

  // Footer link columns: collapsed accordions on phones, open lists on larger screens.
  function initFooter() {
    const cols = $$('[data-footer-col]');
    if (!cols.length) return;
    const mq = window.matchMedia('(min-width: 750px)');
    const sync = () => cols.forEach((d) => { d.open = mq.matches; });
    sync();
    mq.addEventListener ? mq.addEventListener('change', sync) : mq.addListener(sync);
    cols.forEach((d) => d.querySelector('summary').addEventListener('click', (e) => { if (mq.matches) e.preventDefault(); }));
  }

  function boot() {
    Cart.init();
    StickyBar.init();
    initHeader();
    initAnnouncement();
    initFooter();
    initSections();
    // Restore the cart drawer state when navigating back from checkout.
    window.addEventListener('pageshow', (e) => { if (e.persisted) Cart.refresh(); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor support
  document.addEventListener('shopify:section:load', (e) => {
    initSections(e.target);
    if (e.target.querySelector('[data-cbar]')) StickyBar.init();
  });

  window.SAPTheme = { Cart, formatMoney };
})();
